import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ContactForm } from '../components/ContactForm';
import { useContact, useCreateContact, useUpdateContact } from '../queries';
import { contactName, type ContactInput } from '../types';

export function ContactFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const contactId = publicId;
  const navigate = useNavigate();

  const contactQuery = useContact(isEdit ? contactId : '');
  const create = useCreateContact();
  const update = useUpdateContact(contactId);

  async function handleSubmit(values: ContactInput) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    navigate(`/crm/contacts/${saved.public_id}`);
  }

  if (isEdit && contactQuery.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const contact = contactQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/crm/contacts/${contactId}` : '/crm/contacts')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${contact ? contactName(contact) : 'contact'}` : 'New contact'}
        </h1>
      </div>

      <ContactForm
        submitLabel={isEdit ? 'Save changes' : 'Create contact'}
        defaultValues={
          contact
            ? {
                first_name: contact.first_name,
                last_name: contact.last_name,
                email: contact.email,
                phone_number: contact.phone_number,
                title: contact.title,
                company: contact.company ? String(contact.company) : '',
                notes: contact.notes,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/crm/contacts/${contactId}` : '/crm/contacts')}
      />
    </div>
  );
}
